import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, maxLength, required, requiredError, validate } from '@angular/forms/signals';
import { finalize } from 'rxjs';
import { LucideModule } from '../../../../../../shared/modules/lucide.module';
import { conferirArquivoSic, PedidoSicPublicoResponse } from '../../../../models/pedido-sic-acompanhamento.model';
import { formatarTamanho } from '../../../../pages-servicos/solicitacao-servico.store';
import {
  ErroComplementoSic,
  mapearErroComplementoSic,
  PedidoSicAcompanhamentoService,
} from '../../../../services/pedido-sic-acompanhamento.service';
import { ComplementoPedidoSic } from '../../../../services/solicitacoes.service';

const MENSAGEM_TEXTO_OBRIGATORIO = 'Escreva a resposta ao complemento.';

/** O teto da resposta — o mesmo do servidor. */
export const LIMITE_TEXTO_COMPLEMENTO_SIC = 4000;

interface ArquivoComplemento {
  id: number;
  arquivo: File;
  tamanho: string;
}

/** Contador das instâncias, para os ids do card não colidirem entre a página e o modal. */
let sequencia = 0;

/**
 * O card *"O órgão pediu um complemento"* do pedido do e-SIC — no detalhe de `/acompanhamento` e
 * no da aba *Minhas solicitações*. Responde pelo **protocolo**, sem login: o pedido pode ser
 * anônimo.
 *
 * Diferente do card da Carta de Serviço, as regras de anexo **vêm na resposta** (a configuração
 * do departamento atual do pedido): o seletor só aparece quando o departamento permite, e cada
 * arquivo é conferido contra a quantidade, o tamanho e as extensões dele antes do envio.
 *
 * O erro fica no próprio card, sem SweetAlert, e o texto e os arquivos ficam onde estavam.
 */
@Component({
  selector: 'app-complemento-pedido-sic',
  templateUrl: './complemento-pedido-sic.component.html',
  styleUrls: ['../complemento-solicitacao/complemento-solicitacao.component.css'],
  imports: [LucideModule, FormField],
})
export class ComplementoPedidoSicComponent {
  private readonly pedidoSic = inject(PedidoSicAcompanhamentoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly prefixoId = `complemento-pedido-sic-${++sequencia}`;

  readonly protocolo = input.required<string>();

  readonly complemento = input.required<ComplementoPedidoSic>();

  /** O pedido já atualizado pela API — quem o troca na tela e avisa o cidadão é o detalhe. */
  readonly enviado = output<PedidoSicPublicoResponse>();

  readonly limiteTexto = LIMITE_TEXTO_COMPLEMENTO_SIC;

  readonly accept = computed(() => this.complemento().extensoes.map((extensao) => `.${extensao}`).join(','));

  readonly descricaoFormatos = computed(() => this.complemento().formatos.join(', '));

  readonly dados = signal({ texto: '' });

  readonly complementoForm = form(this.dados, (caminho) => {
    required(caminho.texto, { message: MENSAGEM_TEXTO_OBRIGATORIO });
    validate(caminho.texto, ({ value }) =>
      value() && !value().trim() ? requiredError({ message: MENSAGEM_TEXTO_OBRIGATORIO }) : null,
    );
    maxLength(caminho.texto, LIMITE_TEXTO_COMPLEMENTO_SIC, {
      message: `A resposta deve ter no máximo ${LIMITE_TEXTO_COMPLEMENTO_SIC} caracteres.`,
    });
  });

  readonly caracteres = computed(() => this.dados().texto.length);

  readonly arquivos = signal<readonly ArquivoComplemento[]>([]);

  readonly limiteAtingido = computed(() => this.arquivos().length >= this.complemento().quantidadeMaxima);

  /** Os arquivos recusados na última seleção, com o motivo. A API nem chega a ser chamada. */
  readonly recusas = signal<readonly string[]>([]);

  readonly enviando = signal(false);

  readonly erro = signal<ErroComplementoSic | null>(null);

  private proximoId = 1;

  aoSelecionar(evento: Event): void {
    const campo = evento.target as HTMLInputElement;

    this.adicionar(campo.files);
    campo.value = '';
  }

  remover(id: number): void {
    this.arquivos.update((atuais) => atuais.filter((item) => item.id !== id));
    this.recusas.set([]);
  }

  enviar(evento: Event): void {
    evento.preventDefault();

    if (this.enviando()) return;

    this.erro.set(null);

    if (!this.complementoForm().valid()) {
      this.complementoForm().markAsTouched();
      return;
    }

    this.enviando.set(true);

    this.pedidoSic
      .enviarComplemento(this.protocolo(), this.dados().texto, this.arquivos().map((item) => item.arquivo))
      .pipe(
        finalize(() => this.enviando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (resposta) => this.enviado.emit(resposta),
        error: (erro: unknown) => this.erro.set(mapearErroComplementoSic(erro)),
      });
  }

  private adicionar(lista: FileList | null): void {
    if (!lista?.length) return;

    const regras = this.complemento();
    const recusas: string[] = [];
    const aceitos: ArquivoComplemento[] = [];

    for (const arquivo of Array.from(lista)) {
      if (this.arquivos().length + aceitos.length >= regras.quantidadeMaxima) {
        recusas.push(`A resposta aceita no máximo ${regras.quantidadeMaxima} arquivo(s).`);
        break;
      }

      const recusa = conferirArquivoSic(arquivo, regras);

      if (recusa) {
        recusas.push(recusa);
        continue;
      }

      aceitos.push({ id: this.proximoId++, arquivo, tamanho: formatarTamanho(arquivo.size) });
    }

    this.arquivos.update((atuais) => [...atuais, ...aceitos]);
    this.recusas.set(recusas);
  }
}
